import { Controller } from "@hotwired/stimulus"
import Tribute from "tributejs"

// Connects to data-controller="slack-float"
export default class extends Controller {
  static targets = ["window", "messages", "form", "input", "loading", "error", "button", "submitButton"]
  static values = {
    loadUrl: String,
    postUrl: String,
    usersUrl: String,
    reactionUrl: String,
    open: { type: Boolean, default: false }
  }

  connect() {
    // 初期状態は閉じている
    this.openValue = false
    this.tribute = null
    this.slackUsers = []

    // 外側クリックで閉じるためのイベントリスナー
    // クリックの起点はmousedownの時点で記録する（handlePointerDown参照）
    this.pointerDownInsideMenu = false
    this.handlePointerDown = this.handlePointerDown.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('mousedown', this.handlePointerDown, true)
    document.addEventListener('click', this.handleClickOutside)

    // メンションメニューはTributeではなく自前で配置する（positionMentionMenu参照）
    this.repositionMentionMenu = this.repositionMentionMenu.bind(this)
    window.addEventListener('scroll', this.repositionMentionMenu, { passive: true, capture: true })
    window.addEventListener('resize', this.repositionMentionMenu, { passive: true })
  }

  disconnect() {
    // Tributeをクリーンアップ
    if (this.tribute) {
      this.tribute.detach(this.inputTarget)
    }
    // イベントリスナーを削除
    document.removeEventListener('mousedown', this.handlePointerDown, true)
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('scroll', this.repositionMentionMenu, { capture: true })
    window.removeEventListener('resize', this.repositionMentionMenu)
    this.teardownMentionMenuWatcher()
  }

  // Tributeはメンションの選択をmousedownの時点で行い、そこでメニューを
  // display:noneにする。そのため後続のclickイベントのtargetはメニュー外の要素になり、
  // clickだけでは「メニューをクリックしたか」を判定できない。
  handlePointerDown(event) {
    this.pointerDownInsideMenu = !!this.tribute?.menu?.contains(event.target)
  }

  handleClickOutside(event) {
    const startedInsideMenu = this.pointerDownInsideMenu
    this.pointerDownInsideMenu = false

    if (!this.openValue) return

    // クリックがフロートウィンドウ内かボタン内かを判定
    const isInsideWindow = this.hasWindowTarget && this.windowTarget.contains(event.target)
    const isInsideButton = this.hasButtonTarget && this.buttonTarget.contains(event.target)
    // メンションメニューはbody直下に生成されるため、別途判定する
    const isInsideMentionMenu = startedInsideMenu || !!this.tribute?.menu?.contains(event.target)

    if (!isInsideWindow && !isInsideButton && !isInsideMentionMenu) {
      this.close()
    }
  }

  toggle(event) {
    event.preventDefault()
    if (this.openValue) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.openValue = true
    this.windowTarget.classList.remove('d-none')
    this.buttonTarget.classList.add('d-none')
    this.loadMessages()
    this.loadUsers()
  }

  close() {
    this.openValue = false
    this.windowTarget.classList.add('d-none')
    this.buttonTarget.classList.remove('d-none')
    // ウィンドウを閉じてもメニューはbody直下に残るため明示的に閉じる
    this.tribute?.hideMenu()
  }

  async loadUsers() {
    // 既に取得済みの場合はスキップ
    if (this.slackUsers.length > 0) return

    try {
      const response = await fetch(this.usersUrlValue, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        this.slackUsers = await response.json()
        // inputTargetが既にあれば初期化
        this.setupTribute()
      }
    } catch (error) {
      console.error('Failed to load Slack users:', error)
    }
  }

  initTribute() {
    this.tribute = new Tribute({
      trigger: '@',
      values: this.slackUsers.map(user => ({
        key: user.display_name || user.name,
        value: user.id,
        name: user.name,
        displayName: user.display_name,
        avatarUrl: user.avatar_url
      })),
      selectTemplate: (item) => {
        // 表示用に @username を挿入する。IDへの変換は送信時にまとめて行う
        return `@${item.original.displayName || item.original.name}`
      },
      menuItemTemplate: (item) => {
        const displayName = item.original.displayName
        const name = item.original.name
        const avatarUrl = item.original.avatarUrl

        const avatarHtml = avatarUrl
          ? `<img src="${avatarUrl}" class="slack-mention-avatar" alt="">`
          : `<span class="slack-mention-avatar-placeholder"><i class="bi bi-person"></i></span>`

        const nameHtml = displayName && displayName !== name
          ? `<span class="slack-mention-name">${displayName}</span><span class="slack-mention-subname">${name}</span>`
          : `<span class="slack-mention-name">${name}</span>`

        return `<div class="slack-mention-item">${avatarHtml}<div class="slack-mention-info">${nameHtml}</div></div>`
      },
      noMatchTemplate: () => '<span class="text-muted small px-2">該当なし</span>',
      fillAttr: 'value',
      containerClass: 'tribute-container slack-mention-container',
      itemClass: 'tribute-item',
      selectClass: 'tribute-selected',
      menuContainer: document.body,
      // Tributeの座標計算はbodyがスクロールコンテナである前提で、
      // ページがビューポートより高いとメニューが大きく上方向にズレる。
      // 自前でtextareaに追従させるため無効化する（positionMentionMenu参照）
      positionMenu: false
    })

    this.tribute.attach(this.inputTarget)
  }

  // メンションメニューをtextareaの直上（下に余白があれば直下）に固定表示する。
  // Tributeはメニューをbody直下にposition:absoluteで置くが、bottom値をbodyの
  // 高さ基準で算出する一方、実際の基準はビューポートサイズの初期包含ブロックのため、
  // ページがビューポートより高いと (bodyの高さ - ビューポート高) 分だけ上にズレる。
  positionMentionMenu() {
    const menu = this.tribute?.menu
    if (!menu || !this.tribute.isActive || !this.hasInputTarget) return

    const rect = this.inputTarget.getBoundingClientRect()
    const gap = 4
    const spaceAbove = rect.top - gap
    const spaceBelow = window.innerHeight - rect.bottom - gap
    // メニューの実測高に依存すると初回描画時に判定がぶれるため、空きスペースだけで決める
    const showAbove = spaceAbove > spaceBelow

    menu.style.position = 'fixed'
    menu.style.display = 'block'
    menu.style.left = `${rect.left}px`
    menu.style.right = 'auto'
    menu.style.maxHeight = `${Math.min(200, Math.max(spaceAbove, spaceBelow))}px`

    if (showAbove) {
      menu.style.top = 'auto'
      menu.style.bottom = `${window.innerHeight - rect.top + gap}px`
    } else {
      menu.style.bottom = 'auto'
      menu.style.top = `${rect.bottom + gap}px`
    }
  }

  // Tributeはメニュー表示のたびにstyleを display:block だけに戻すため、
  // その直後（描画前）に再配置する
  repositionMentionMenu() {
    if (!this.tribute?.isActive) return
    requestAnimationFrame(() => this.positionMentionMenu())
  }

  setupMentionMenuWatcher() {
    this.teardownMentionMenuWatcher()
    this.mentionMenuElement = this.inputTarget
    this.mentionMenuElement.addEventListener('tribute-active-true', this.repositionMentionMenu)
    this.mentionMenuElement.addEventListener('input', this.repositionMentionMenu)
  }

  teardownMentionMenuWatcher() {
    if (this.mentionMenuElement) {
      this.mentionMenuElement.removeEventListener('tribute-active-true', this.repositionMentionMenu)
      this.mentionMenuElement.removeEventListener('input', this.repositionMentionMenu)
    }
    this.mentionMenuElement = null
  }

  async loadMessages() {
    this.showLoading()
    this.hideError()

    try {
      const response = await fetch(this.loadUrlValue, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const html = await response.text()
        const contentEl = this.element.querySelector('#slack_float_content')
        if (contentEl) {
          contentEl.innerHTML = html
        }
        this.scrollToBottom()
        // DOM更新後にTributeを初期化/再アタッチ
        this.setupTribute()
        // テキストエリアにフォーカス
        this.focusInput()
      } else {
        this.showError('Failed to load messages')
      }
    } catch (error) {
      console.error('Slack float error:', error)
      this.showError('Connection error')
    } finally {
      this.hideLoading()
    }
  }

  setupTribute() {
    // ユーザーデータがない、またはinputTargetがない場合はスキップ
    if (this.slackUsers.length === 0 || !this.hasInputTarget) return

    if (this.tribute) {
      // 既にTributeが初期化されている場合は再アタッチ
      this.tribute.attach(this.inputTarget)
    } else {
      // 初期化
      this.initTribute()
    }

    // textareaはDOM入れ替えのたびに別要素になるため、監視も張り直す
    this.setupMentionMenuWatcher()
    this.retriggerPendingMention()
  }

  // メンション候補の取得はメッセージ一覧の取得と並行して走るため、
  // 候補が揃う前に "@" を入力するとTributeが未アタッチで取りこぼされる。
  // アタッチ直後にkeyupを合成し、キャレット位置をTributeに再評価させる。
  retriggerPendingMention() {
    if (!this.hasInputTarget || document.activeElement !== this.inputTarget) return
    if (!this.inputTarget.value.includes('@')) return

    this.inputTarget.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }))
  }

  async submit(event) {
    event.preventDefault()

    let text = this.inputTarget.value.trim()
    if (!text) return

    // @username を <@U12345678> 形式に変換
    text = this.convertMentionsForSlack(text)

    this.showSubmitLoading()
    this.hideError()

    try {
      const formData = new FormData()
      formData.append('text', text)

      const response = await fetch(this.postUrlValue, {
        method: 'POST',
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        const contentEl = this.element.querySelector('#slack_float_content')
        if (contentEl) {
          contentEl.innerHTML = html
        }
        this.inputTarget.value = ''
        this.inputTarget.style.height = 'auto'
        // DOM更新後にスクロールとTribute再アタッチ
        setTimeout(() => {
          this.scrollToBottom()
          this.setupTribute()
        }, 100)
      } else {
        this.showError('Failed to send message')
      }
    } catch (error) {
      console.error('Slack post error:', error)
      this.showError('Connection error')
    } finally {
      this.hideSubmitLoading()
    }
  }

  refresh(event) {
    event.preventDefault()
    this.loadMessages()
  }

  async addReaction(event) {
    event.preventDefault()
    const button = event.currentTarget
    const emoji = button.dataset.emoji
    const messageEl = button.closest('.slack-message')
    const ts = messageEl?.dataset.messageTs

    if (!emoji || !ts) return

    // ボタンを一時的に無効化
    button.disabled = true

    try {
      const formData = new FormData()
      formData.append('ts', ts)
      formData.append('emoji', emoji)

      const response = await fetch(this.reactionUrlValue, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      const result = await response.json()

      if (result.ok) {
        // メッセージを再読み込みしてリアクションを更新
        this.loadMessages()
      } else {
        console.error('Reaction error:', result.error)
      }
    } catch (error) {
      console.error('Reaction error:', error)
    } finally {
      button.disabled = false
    }
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }
  }

  focusInput() {
    if (this.hasInputTarget) {
      this.inputTarget.focus()
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('d-none')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('d-none')
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('d-none')
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('d-none')
    }
  }

  showSubmitLoading() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.querySelector('.submit-icon')?.classList.add('d-none')
      this.submitButtonTarget.querySelector('.submit-spinner')?.classList.remove('d-none')
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
    }
  }

  hideSubmitLoading() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.querySelector('.submit-icon')?.classList.remove('d-none')
      this.submitButtonTarget.querySelector('.submit-spinner')?.classList.add('d-none')
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false
    }
  }

  // キーボードショートカット
  keydown(event) {
    // Tributeのメニューが開いている場合は送信しない
    if (this.tribute && this.tribute.isActive) {
      return
    }
    // Ctrl+Enter or Cmd+Enter で送信
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault()
      this.submit(event)
    }
  }

  // テキストエリアの自動リサイズ
  autoResize(event) {
    const textarea = event.target
    textarea.style.height = 'auto'
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
  }

  // @username を <@U12345678> 形式に変換する。
  // 候補一覧から解決するため、メニューから選ばずに手打ちした @名前 も変換される。
  convertMentionsForSlack(text) {
    const candidates = this.mentionCandidates()
    if (candidates.length === 0) return text

    let result = ''
    let index = 0

    while (index < text.length) {
      // メールアドレス等を誤変換しないよう、Slackと同じく行頭か空白の直後のみ対象にする
      const isTrigger = text[index] === '@' && (index === 0 || /\s/.test(text[index - 1]))

      if (isTrigger) {
        const rest = text.slice(index + 1).toLowerCase()
        const hit = candidates.find(candidate => rest.startsWith(candidate.label))

        if (hit) {
          result += `<@${hit.id}>`
          // 変換済みのIDを再走査しないよう、名前のぶんだけ進める
          index += 1 + hit.label.length
          continue
        }
      }

      result += text[index]
      index += 1
    }

    return result
  }

  // メンション解決用の候補。表示名と本名の両方を受け付ける
  mentionCandidates() {
    const candidates = []

    for (const user of this.slackUsers) {
      for (const label of [user.display_name, user.name]) {
        if (label) candidates.push({ label: label.toLowerCase(), id: user.id })
      }
    }

    // 「@山田」が「@山田太郎」より先にマッチしないよう、長い名前から試す
    return candidates.sort((a, b) => b.label.length - a.label.length)
  }
}
